import React, { useState, useEffect } from 'react';
import config from '../config';
import ComparisonPatentCard from './ComparisonPatentCard';
import SearchAndAddModal from './SearchAndAddModal';
import ComparePanel from './ComparePanel';
import './ComparisonWorkspace.css';
import BackButton from './BackButton';

export default function ComparisonWorkspace({ user, setPage }) {
    const token = localStorage.getItem('token');
    const [patents, setPatents] = useState([]);
    const [yourPatentNumber, setYourPatentNumber] = useState(null);
    const [showSearchModal, setShowSearchModal] = useState(false);
const loadSavedWorkspace = async (loadData) => {
    try {
        // Fetch full patent details for each patent number
        const patentsToLoad = [];
        for (const pn of loadData.patentNumbers) {
            const response = await fetch(`${config.API_URL}/api/search/patent-number/${pn}`);
            const data = await response.json();
            if (data.results && data.results.length > 0) {
                const p = data.results[0];
                patentsToLoad.push({
                    patent_number: p.patent_number,
                    id: p.patent_number,
                    title: p.title,
                    abstract: p.abstract,
                    assignee: p.assignee,
                    filing_date: p.filing_date,
                    grant_date: p.grant_date,
                    status: p.status || 'Live',
                    ipc_codes: p.ipc_codes || [],
                    cpc_codes: p.cpc_codes || []
                });
            }
        }
        
        setPatents(patentsToLoad);
        setYourPatentNumber(loadData.yourPatentNumber);
        
        // If there's saved AI analysis, show the compare panel with it
        if (loadData.ai_analysis) {
            setSavedAiAnalysis(loadData.ai_analysis);
            setShowComparePanel(true);
        }
    } catch (error) {
        console.error('Error loading saved workspace:', error);
        alert('Failed to load saved workspace');
    }
};
    const [showComparePanel, setShowComparePanel] = useState(false);
    const [savedAiAnalysis, setSavedAiAnalysis] = useState(null);

    // Load saved patents from localStorage on mount
    useEffect(() => {
    // First check if loading from a saved workspace
    const savedWorkspaceLoad = localStorage.getItem('comparisonWorkspaceLoad');
    if (savedWorkspaceLoad) {
        try {
            const loadData = JSON.parse(savedWorkspaceLoad);
            loadSavedWorkspace(loadData);
            localStorage.removeItem('comparisonWorkspaceLoad');
            return;
        } catch (error) {
            console.error('Error loading saved workspace:', error);
        }
    }

    // Otherwise load from regular workspace storage
    const saved = localStorage.getItem('comparisonWorkspacePatents');
    if (saved) {
        try {
            const parsed = JSON.parse(saved);
            setPatents(parsed.patents || []);
            setYourPatentNumber(parsed.yourPatentNumber || null);
        } catch (error) {
            console.error('Error loading saved comparison workspace:', error);
        }
    }
}, []);

    // Save patents to localStorage whenever they change
    useEffect(() => {
        if (patents.length > 0 || yourPatentNumber) {
            localStorage.setItem('comparisonWorkspacePatents', JSON.stringify({
                patents,
                yourPatentNumber
            }));
        }
    }, [patents, yourPatentNumber]);

    const handleAddPatent = (patent) => {
        // Check if patent already exists
        const patentNumber = patent.patent_number || patent.id;
        const exists = patents.some(p => (p.patent_number || p.id) === patentNumber);
        
        if (exists) {
            alert('This patent is already in your comparison workspace');
            return;
        }

        // Check maximum limit (5 patents for comparison)
        if (patents.length >= 5) {
            alert('Maximum 5 patents can be compared at once. Remove a patent to add a new one.');
            return;
        }

        setPatents([...patents, patent]);
    };

    const handleRemovePatent = (patentNumber) => {
        setPatents(patents.filter(p => (p.patent_number || p.id) !== patentNumber));
        
        // If removing "your patent", clear that designation
        if (yourPatentNumber === patentNumber) {
            setYourPatentNumber(null);
        }
    };

    const handleMarkAsYours = (patentNumber) => {
        setYourPatentNumber(patentNumber);
    };

    const handleClearAll = () => {
        if (window.confirm('Are you sure you want to remove all patents from the comparison workspace?')) {
            setPatents([]);
            setYourPatentNumber(null);
            localStorage.removeItem('comparisonWorkspacePatents');
        }
    };

    const handleGenerateAnalysis = () => {
        if (patents.length < 2) {
            alert('Please add at least 2 patents to generate a comparison analysis');
            return;
        }

        if (!user || user.tier === 'free') {
            alert('AI comparison analysis requires a Premium or Pro account. Please upgrade to use this feature.');
            return;
        }

        setShowComparePanel(true);
    };

const handleSaveWorkspace = async () => {
    const name = prompt('Enter a name for this workspace comparison:', 'Comparison ' + new Date().toLocaleDateString());
    if (!name) return;

    const token = localStorage.getItem('token');
    if (!token) {
        alert('Please log in to save workspace comparisons');
        return;
    }

    try {
        const response = await fetch(`${config.API_URL}/api/save-workspace-comparison?token=${token}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                name: name,
                patent_numbers: patents.map(p => p.patent_number || p.id),
                your_patent_number: yourPatentNumber,
                ai_analysis: null // Saving workspace only, no AI analysis yet
            })
        });

        const data = await response.json();
        if (response.ok) {
            alert('Workspace saved successfully!');
        } else {
            alert('Failed to save: ' + (data.detail || 'Unknown error'));
        }
    } catch (error) {
        alert('Error saving workspace: ' + error.message);
    }
};

    const getExistingPatentNumbers = () => {
        return patents.map(p => p.patent_number || p.id);
    };

    return (
        <div className="comparison-workspace">
            <div className="workspace-header">
    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <BackButton onClick={() => setPage('search')} />
            <div>
                <h1 className="workspace-title">Comparison Workspace</h1>
                <p style={{ color: '#64748b', fontSize: '14px', margin: '8px 0 0 0' }}>
                    Build cross-organization patent comparisons • {patents.length}/5 patents added
                </p>
            </div>
        </div>

                <div className="workspace-actions">
                    <button 
                        className="add-patent-btn"
                        onClick={() => setShowSearchModal(true)}
                    >
                        + Add Patent
                    </button>
                    {patents.length >= 2 && (
                        <button
                            onClick={() => setShowComparePanel(true)}
                            style={{
                                background: '#0ea5e9',
                                color: 'white',
                                border: 'none',
                                padding: '12px 24px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '16px',
                                fontWeight: '600'
                            }}
                        >
                            View Side-by-Side
                        </button>
                    )}
                    {patents.length >= 2 && (
                        <button 
                            className="generate-analysis-btn"
                            onClick={handleGenerateAnalysis}
                            disabled={!user || user.tier === 'free'}
                        >
                            Generate AI Analysis
                        </button>
                    )}
                    {patents.length >= 2 && (
                        <button
                            onClick={handleSaveWorkspace}
                            style={{
                                background: '#10b981',
                                color: 'white',
                                border: 'none',
                                padding: '12px 24px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '16px',
                                fontWeight: '600'
                            }}
                        >
                            Save Workspace
                        </button>
                    )}
                    {patents.length > 0 && (
                        <button 
                            className="clear-all-btn"
                            onClick={handleClearAll}
                        >
                            Clear All
                        </button>
                    )}
                </div>
            </div>

            {patents.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-icon">📊</div>
                    <h3>No Patents in Workspace</h3>
                    <p>
                        Start building your cross-organization comparison by adding patents from different companies.
                        <br />
                        Perfect for due diligence, licensing analysis, and competitive intelligence.
                    </p>
                    <button 
                        className="add-patent-btn"
                        onClick={() => setShowSearchModal(true)}
                    >
                        + Add Your First Patent
                    </button>
                </div>
            ) : (
                <div className="patents-grid">
                    {patents.map((patent) => (
                        <ComparisonPatentCard
                            key={patent.patent_number || patent.id}
                            patent={patent}
                            onRemove={handleRemovePatent}
                            isYourPatent={(patent.patent_number || patent.id) === yourPatentNumber}
                            onMarkAsYours={handleMarkAsYours}
                        />
                    ))}
                </div>
            )}

            {/* Search and Add Modal */}
            <SearchAndAddModal
                isOpen={showSearchModal}
                onClose={() => setShowSearchModal(false)}
                onAddPatent={handleAddPatent}
                existingPatentNumbers={getExistingPatentNumbers()}
                token={token}
            />

            {/* Compare Panel - Enhanced with "Your Patent" Context */}
{showComparePanel && (
    <ComparePanel
        showComparePanel={showComparePanel}
        setShowComparePanel={setShowComparePanel}
        compareList={patents}
        savedAiAnalysis={savedAiAnalysis}
        setSavedAiAnalysis={setSavedAiAnalysis}
    />
)}
        </div>
    );
}



